<script setup lang="ts">
import {
    faArrowLeft,
    faEdit,
    faCircleInfo,
    faFloppyDisk,
    faXmark, faTriangleExclamation
} from "@fortawesome/pro-regular-svg-icons";
import { useAssetSettings, useAssetSettingsLookups, useSaveAssetSettings, useAssetCustomers } from "@/composables/assets/assets";
import { computed, reactive, ref, toRef, watch } from 'vue'
import { useRouter } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ModalContainer from "@/components/modal/modal-container.vue";
import ModalTitle from '@/components/modal/modal-title.vue';
import ModalContent from '@/components/modal/modal-content.vue';
import ModalFooter from '@/components/modal/modal-footer.vue';
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import Button from "@/components/button-secondary.vue";
import ButtonPrimary from "@/components/button-primary.vue";
import InvoiceDetailCard from "@/views/assetSetup/invoice-details-card.vue"
import TariffProfileCard from "@/views/assetSetup/tariff-profile-card.vue";
import BillingSettingsCard from "@/views/assetSetup/billing-settings-card.vue"
import ContractTermsCard from "@/views/assetSetup/contract-terms-card.vue"
import UtilityProfileCard from "@/views/assetSetup/utility-profile-card.vue"
import InvoiceContactCard from "@/views/assetSetup/invoice-contact-card.vue"
import ReconcilliationCard from "@/views/assetSetup/reconciliation-card.vue";
import { type Contact } from "@/api/contacts/schemas";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import useLoader from "@/composables/loader";
import useNotifications from "@/composables/notifications";
import { useUpdateContacts } from '@/composables/contacts/contacts';
import { useSaveTariffs } from "@/composables/billing/tariffs";
import { useTariffsForAsset } from "@/composables/billing/tariffs";
import { createLogger } from '@/helpers/logging'
import { useContacts } from '@/composables/contacts/contacts';
import { CONTACT_TYPES } from "@/api/constants";
import GridContainer from '@/components/grid-container.vue'
import GridItem from '@/components/grid-item.vue'
import ModalEditManualAsset  from '@/views/assetSetup/modals/modal-edit-manual-asset.vue'
import { useModalState } from '@/shared/composables/useModalState'
import { AssetDeliveryStatus, AssetType } from '@/features/assets/constants'
import { buildUpdateAssetSettingsSchema } from '@/features/assets/schemas'
import { useQuery } from '@tanstack/vue-query'
import { assetQueries } from '@/features/assets/queries'

const logger = createLogger('views:assetSetup/setup.vue')
const { wrap: wrapInLoader } = useLoader()
const { addError, addSuccess, addWarning } = useNotifications()
const router = useRouter();
const contractFundEntityId = ref<number>();
const selectedCustomerId = ref<string | null>();
const showGlobalError = ref<boolean>(false);

const props = defineProps<{ assetCode: string }>()
const assetCode = toRef(props, 'assetCode')
const { data: assetDetails } = useQuery(assetQueries.details(assetCode))
const { mutateAsync: mutateContactsAsync } = useUpdateContacts(assetCode);
const { data: assetData, isError,error:assetDataError } = useAssetSettings(assetCode);
const { isError:isLookupsError, error:lookupsError  } = useAssetSettingsLookups();
const { data: fetchedContacts, isError:isContactsError, error: contactError } = useContacts(assetCode,CONTACT_TYPES.invoice);
const { isError: isAssetTariffsError, error: assetTariffsError } = useTariffsForAsset(assetCode)
const { mutateAsync } = useSaveAssetSettings(assetCode)
const { mutateAsync: mutateTariffsAsync } = useSaveTariffs(assetCode)
const { isError:isCustomerError, error:customerError} = useAssetCustomers(contractFundEntityId);
const showModalValidation = ref(false);
const showModalLeave = ref<string>();
const contacts = ref<Contact[]>([]);
const isExportingPower = ref(false)
const isAutoApproving = ref(false)
const manualInputModalState = reactive(useModalState())
watch(fetchedContacts, (newContacts) => {
  if (newContacts) {
    contacts.value = [...newContacts]; // Ensure reactivity
  }
}, { immediate: true });

const { handleSubmit, errors, setFieldError, values: fieldValues, setFieldValue } = useForm({
  initialValues: {
    tariffProfile: {
      rates: {}
    },
    contacts:[]
  },
  validationSchema: computed(
    () => toTypedSchema(buildUpdateAssetSettingsSchema(isExportingPower.value, isAutoApproving.value))
  )
})

defineOptions({ name: 'AssetSetup' })

watch(
  assetData,
  x => setFieldValue(
    'assetType',
    x?.assetType === AssetType.Solar || x?.assetType === AssetType.Wheeling ? x.assetType : undefined
  ),
  { immediate: true }
)

const updateInvoiceContact = (details: Contact[]) => {

  contacts.value = [...details];
}

const acceptAndSave = async () => {
    showModalValidation.value = false

    await validateAndSave();

    if (Object.keys(errors.value).length < 1) {
      router.push({ name: 'assets.setupInvoice', params: { assetCode: assetCode.value } })
    }
}
const contactsCustomValidation =()=>{
    if(contacts.value.length==0){
      addError({ title: 'You need to add atleast one contact.' })
      return false
    } else if(contacts.value.length>=1 && contacts.value.filter(x=>{return x.primaryContact}).length==0){
     addError({ title: 'You need to set primary contact.' })
     return false
    }
    return true
}
const validateAndSave = handleSubmit(
  async values => {
    try {
      if (values.assetType !== AssetType.Wheeling && values.assetType !== AssetType.Solar) {
        setFieldError('assetType', 'Invalid asset type encountered. Expected one of `Wheeling` or `Solar`.')
        return
      }

      const plainContacts = contacts.value.map(contact => ({
        ...contact // Ensures new/edited contacts are plain objects
      }));
      if (!contactsCustomValidation()) {
        setFieldError('contacts', 'Contacts failed validation.')

        return
      }
     // const settings = assetData.value!
      contractFundEntityId.value =values.invoiceDetails.contractFundEntityId;
      selectedCustomerId.value = values.invoiceDetails.customerExternalIdentifier;

      const invoiceDetails = {
        contractFundEntityId: contractFundEntityId.value,
        customerExternalIdentifier: selectedCustomerId.value
      }

      const updates = await wrapInLoader(
        async () => Promise.allSettled([
          (assetData.value?.assetType === AssetType.Solar 
            ? mutateTariffsAsync({
                ...values.tariffProfile,
                exportPower: isExportingPower.value,
                rates: values.assetType === AssetType.Solar
                  ? Object.entries(values.tariffProfile.rates).map(
                    ([tariffType, rates]) => ({ tariffTypeId: parseInt(tariffType, 10), pvRate: rates.pvRate, exportRate: rates.exportRate })
                  )
                  : [],
              })
            : Promise.resolve()
          ),
          mutateContactsAsync(plainContacts),
        ]).then(
          // This is dirty. There are race conditions in the backend when attempting to update the asset settings
          // and the tariffs. The EskomGenerationWheelingCharge is being overwritten to `null` by the asset settings
          // updates. So we'll update the tariffs & contacts first, then the settings.
          settled => Promise.allSettled([
            ...settled,
            mutateAsync({
              assetType: values.assetType,
              invoiceDetails,
              billingSettings: values.billingSettings,
              utilityProvider: {
                utilityProviderId: values.utilityProvider.utilityProviderId,
                tariffStructureId: values.utilityProvider.tariffStructureId,
                blendedTariff: values.utilityProvider.blendedTariff,
              },
              contractTerms: values.contractTerms,
            })
          ])
        )
      )

      // All updates were successful.
      if (updates.every(x => x.status === 'fulfilled')) {
        addSuccess({ title: 'Asset settings updated successfully.' })
      } else if (updates.every(x => x.status === 'rejected')) {
        addError({ title: 'Failed to save asset settings.' })
      } else {
        addWarning({
          title: 'Asset settings were partially updated.',
          message: 'Some settings failed to save correctly. Please check your input and try again.'
        })
      }

    } catch (e: unknown) {
      addError({ title: 'Failed to save asset settings.' })
    }
  },
  errors => {
    logger.warning('Form still contains validation errors', { errors: errors.errors, values: errors.values })
  }
)
const goBack = () => {
    showModalLeave.value = undefined
    showGlobalError.value = false
    router.push({ name: 'assets.dashboard' })
};
watch([isError,isCustomerError,isLookupsError,isContactsError,isAssetTariffsError], ([hasError,hasCustomerError,hasLookupsError,hasContactsError,hasAssetTariffsError]) => {
  showGlobalError.value=hasError||hasCustomerError||hasLookupsError||hasContactsError||hasAssetTariffsError;
}, { immediate: true });
const globalErrorMessage = computed(() => {
    return [customerError.value?.message??'',assetDataError.value?.message??'',lookupsError.value?.message??'',contactError.value?.message??'', assetTariffsError.value?.message??''].join("\r\n");
});

</script>
<template>
    <ModalEditManualAsset  v-if="manualInputModalState.isOpen" :asset-code="assetCode" @close="manualInputModalState.close()"></ModalEditManualAsset >
    <ModalContainer v-if="showModalValidation">
        <ModalTitle>ENSURE ALL REQUIRED INFO IS PROVIDED</ModalTitle>
        <ModalContent :icon="faTriangleExclamation" icon-variant="warning">
          <div class="d-flex flex-column gap-3">
            <span>The following should please be completed to proceed:</span>
            <div>
              <span><strong>STEP 1:</strong> Link Meters associated with this asset</span>
              <br />
              <span><strong>STEP 2:</strong> Complete all inputs on the Settings tab</span>
              <br />
              <span><strong>STEP 3:</strong> Setup the Invoice Template with all recurring charges</span>
              <br />
            </div>
            <span>
              If meters were linked, they should auto-fill the Invoice Template.
              Review the displayed information to confirm accuracy for future invoices.
            </span>
          </div>
        </ModalContent>
        <ModalFooter class="text-end">
          <ButtonPrimary :icon="faCheck" @click="acceptAndSave">Okay</ButtonPrimary>
        </ModalFooter>
    </ModalContainer>
    <ModalContainer v-if="showModalLeave">
        <ModalTitle>ARE YOU SURE YOU WANT TO LEAVE THIS PAGE?</ModalTitle>
        <ModalContent :icon="faTriangleExclamation" icon-variant="warning">
          {{ showModalLeave }}
          <br /><br />
          Do you want to proceed?
        </ModalContent>
        <ModalFooter class="text-end">
            <Button :icon="faXmark" @click="showModalLeave = undefined" class="color-temp-white">No</Button>
            <ButtonPrimary :icon="faCheck" @click="goBack" class="color-temp-white ms-2">Yes</ButtonPrimary>
        </ModalFooter>
    </ModalContainer>
    <ModalContainer v-if="showGlobalError">
        <ModalTitle>OUR APOLOGIES</ModalTitle>
        <ModalContent :icon="faTriangleExclamation" icon-variant="warning">
          <b>Something went wrong.</b>
          <br/><br/>
          Please try reloading the page, alternatively log the following error with our support team:
          <br/><br/>
          {{ globalErrorMessage }}
          <br/><br/>
          Thank you for your patience.
        </ModalContent>
        <ModalFooter class="text-end">
            <ButtonPrimary :icon="faCheck" @click="goBack" class="color-temp-white">Okay</ButtonPrimary>
        </ModalFooter>
    </ModalContainer>
    <div class="wrapper">
        <div class="d-flex justify-content-between align-items-center top-items-wrapper">
            <div class="button-wrapper">
                <Button :icon="faArrowLeft" @click="showModalLeave = `If you made changes, remember to save before navigating away, alternatively they will be discarded.`" style="  width: 142px;">Back to Dashboard</Button>
                <Button v-if="assetCode.startsWith('M') && assetDetails?.type === AssetType.Solar"
                        :icon="faEdit"
                        @click="manualInputModalState.open()">
                  Edit Manual Inputs
                </Button>
            </div>
            <div class="d-flex align-items-center gap-2"
                v-if="assetDetails?.type === AssetType.Solar && assetData?.deliveryStatus?.name && assetData?.deliveryStatus?.name !== AssetDeliveryStatus.Operational">
                <FontAwesomeIcon :icon="faCircleInfo" class="fa-4x text-yellow" />
                <span>Complete these: <strong>1.</strong> Link Meters for this asset |
                    <strong>2.</strong> Enter all Asset Settings below | <strong>3.</strong> Setup
                    Invoice Template</span>
            </div>
        </div>

        <GridContainer :columns="['407px', 'auto']" :column-gap="27" :row-gap="27" v-if="!showGlobalError">
          <GridContainer :columns="['100%']" :row-gap="27">
              <InvoiceDetailCard :asset-code="assetCode" />
              <BillingSettingsCard :asset-code="assetCode" @update:auto-approve="isAutoApproving = $event" />
              <TariffProfileCard :asset-code="assetCode" @update:is-exporting-power="isExportingPower = $event" />
          </GridContainer>

          <GridContainer :columns="[310, '1fr']" :column-gap="27" :row-gap="27">
            <GridItem span>
              <ContractTermsCard :asset-code="assetCode" />
            </GridItem>
            <GridItem span>
              <InvoiceContactCard :asset-code="assetCode" @update="updateInvoiceContact" />
            </GridItem>
            <GridItem>
              <ReconcilliationCard :asset-code="assetCode"/>
            </GridItem>
            <GridItem />
          </GridContainer>
          <GridItem span>
            <UtilityProfileCard :asset-code="assetCode" />
          </GridItem>
        </GridContainer>

      <div class="button-wrapper mt-4" v-if="!showGlobalError">
            <ButtonPrimary :icon="faFloppyDisk" class="color-temp-white" @click="showModalValidation = true"
                v-if="assetData?.deliveryStatus?.name && assetData?.deliveryStatus?.name != 'Operational'">
                Save & Open
                Invoice Setup</ButtonPrimary>
            <Button :icon="faFloppyDisk" class="one-h-width" @click="validateAndSave">Save</Button>
            <Button :icon="faXmark" class="one-h-width" @click="showModalLeave = `If you cancel with unsaved information, it will be discarded, and you won’t be able to recover it.`">Cancel</Button>
            <template v-if="fieldValues.assetType && errors.assetType">
              <span class="vr"></span>
              <span class="text-danger align-center">{{ errors.assetType }}</span>
            </template>
        </div>
    </div>
</template>
<style scoped lang="scss">
.wrapper {
    .top-items-wrapper {
        margin-bottom: 17px;
        margin-right: 318px;
    }

    .one-h-width {
        width: 100px;
    }

    .button-wrapper {
        display: flex;
        gap: 10px;
        margin-bottom: 24px;
    }
}


</style>
